import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ImportLedgerAccountsResponse } from '~/data/ledgerAccount';

import {
  Alert,
  AlertTitle,
  FormattedNumber,
  KeyInformationRow,
} from '@vestigas/ui';

type P =
  | {
      readonly hasValidationErrors: boolean;
      readonly result: ImportLedgerAccountsResponse;
      readonly variant: 'preview';
    }
  | {
      readonly result: ImportLedgerAccountsResponse;
      readonly variant: 'result';
    };

export function LedgerAccountImportValidationAlert({
  hasValidationErrors,
}: {
  readonly hasValidationErrors: boolean;
}) {
  const { t } = useTranslation('ledgerAccounts');

  return (
    <Alert severity={hasValidationErrors ? 'error' : 'success'}>
      <AlertTitle>
        {hasValidationErrors
          ? t(($) => $.import.preview.invalidHeading)
          : t(($) => $.import.preview.heading)}
      </AlertTitle>
      {hasValidationErrors
        ? t(($) => $.import.preview.invalidRecordsIssue)
        : t(($) => $.import.preview.successBody)}
    </Alert>
  );
}

export function LedgerAccountImportPreview(props: P) {
  const { t } = useTranslation('ledgerAccounts');

  const { result, variant } = props;
  const { hasValidationErrors } =
    'hasValidationErrors' in props ? props : { hasValidationErrors: false };

  const heading =
    variant === 'preview'
      ? t(($) => $.import.preview.heading)
      : t(($) => $.import.result.heading);

  const inputNumbers = useMemo(
    () => [
      {
        label: t(($) => $.import.counts.received, { count: result.received }),
        value: <FormattedNumber value={result.received} />,
      },
      {
        label: t(($) => $.import.counts.companiesUpdated, {
          count: result.affectedCompaniesCount,
        }),
        value: <FormattedNumber value={result.affectedCompaniesCount} />,
      },
    ],
    [result.affectedCompaniesCount, result.received, t],
  );
  const resultNumbers = useMemo(
    () => [
      {
        children: [
          {
            label: t(($) => $.import.counts.accountsUpdated),
            value: <FormattedNumber value={result.importedUpdated} />,
          },
          {
            label: t(($) => $.import.counts.accountsNew),
            value: <FormattedNumber value={result.importedNew} />,
          },
          {
            label: t(($) => $.import.counts.accountsDeleted),
            value: <FormattedNumber value={result.importedDeleted} />,
          },
        ],
        groupName: t(($) => $.import.groups.results),
      },
      {
        children: [
          {
            label: t(($) => $.import.counts.skippedDuplicate, {
              count: result.skippedDuplicate,
            }),
            value: <FormattedNumber value={result.skippedDuplicate} />,
          },
          {
            className:
              result.skippedInvalid > 0
                ? 'text-tiga-semantic-error *:text-tiga-semantic-error!'
                : undefined,
            label: t(($) => $.import.counts.skippedInvalid, {
              count: result.skippedInvalid,
            }),
            value: <FormattedNumber value={result.skippedInvalid} />,
          },
        ],
        groupName: t(($) => $.import.groups.errors),
      },
    ],
    [
      result.importedDeleted,
      result.importedNew,
      result.importedUpdated,
      result.skippedDuplicate,
      result.skippedInvalid,
      t,
    ],
  );
  const items = useMemo(
    () => [...inputNumbers, ...resultNumbers],
    [inputNumbers, resultNumbers],
  );

  return (
    <div className="flex flex-col gap-4">
      {variant === 'preview' ? (
        <LedgerAccountImportValidationAlert
          hasValidationErrors={hasValidationErrors}
        />
      ) : (
        <Alert severity="success">{heading}</Alert>
      )}
      <KeyInformationRow
        className="-mb-2 w-full *:min-w-24"
        hasDividers
        items={items}
        valueSize="large"
      />
    </div>
  );
}
